import { useCallback, useEffect, useRef, useState } from 'react';

import type { ConnectorQueryEcho, ConnectorResource, ExecuteConnectorQuery } from './types';

type DataConnectorState = {
	items: unknown[];
	isLoading: boolean;
	error: Error | null;
	done: boolean;
};

const scheduleIdle = (callback: () => void): (() => void) => {
	if (typeof requestIdleCallback !== 'undefined') {
		// A deadline, so a page with heavy third-party scripts cannot defer the
		// fetch for seconds while the button sits disabled.
		const id = requestIdleCallback(callback, { timeout: 500 });
		return () => cancelIdleCallback(id);
	}
	// Defer to a macrotask rather than running inline, so the fetch never fires
	// re-entrantly during React's commit phase (where requestIdleCallback is
	// unavailable, for example in server render and in jsdom).
	const id = setTimeout(callback, 0);
	return () => clearTimeout(id);
};

/**
 * Generic pagination hook. Restored from the declined spike (commit 8ff2d69fbd,
 * `use-data-connector.ts`), retyped onto the echoed connector query.
 *
 * It knows no content type. It starts from a seed resource and re-runs the echo
 * each response hands back, appending de-duplicated items until the echo is
 * null. With `autoLoad` it walks the whole set on idle, one page for each turn,
 * so a 304-item gallery costs three background requests after a 9-item server
 * render. Client-side filtering and search stay honest, because the browser ends
 * up holding every item.
 */
export const useDataConnector = ({
	initial,
	executeQuery,
	autoLoad = true,
	keyExtractor = defaultKeyExtractor,
}: {
	initial: ConnectorResource;
	executeQuery: ExecuteConnectorQuery;
	autoLoad?: boolean;
	keyExtractor?: (item: unknown) => string | undefined;
}): DataConnectorState & { loadMore: () => void } => {
	const [state, setState] = useState<DataConnectorState>(() => ({
		items: initial.items,
		isLoading: false,
		error: null,
		done: initial.query === null,
	}));

	const mountedRef = useRef(true);
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const keyExtractorRef = useRef(keyExtractor);
	keyExtractorRef.current = keyExtractor;

	const nextQueryRef = useRef<ConnectorQueryEcho | null>(initial.query);
	const seenRef = useRef<Set<string>>(new Set());
	const loadingRef = useRef(false);

	// Reset when the seed or the executor identity changes, for example when a
	// locale switch rebuilds both.
	useEffect(() => {
		nextQueryRef.current = initial.query;
		loadingRef.current = false;
		seenRef.current = new Set();
		for (const item of initial.items) {
			const key = keyExtractorRef.current(item);
			if (key !== undefined) seenRef.current.add(key);
		}
		setState({
			items: initial.items,
			isLoading: false,
			error: null,
			done: initial.query === null,
		});
	}, [initial, executeQuery]);

	const loadMore = useCallback(() => {
		const query = nextQueryRef.current;
		if (!query || loadingRef.current) return;

		loadingRef.current = true;
		setState((prev) => ({ ...prev, isLoading: true, error: null }));

		executeQuery(query)
			.then((page) => {
				if (!mountedRef.current) return;
				const fresh = page.items.filter((item) => {
					const key = keyExtractorRef.current(item);
					if (key === undefined) return true;
					if (seenRef.current.has(key)) return false;
					seenRef.current.add(key);
					return true;
				});
				nextQueryRef.current = page.query;
				loadingRef.current = false;
				setState((prev) => ({
					items: [...prev.items, ...fresh],
					isLoading: false,
					error: null,
					done: page.query === null,
				}));
			})
			.catch((err: unknown) => {
				if (!mountedRef.current) return;
				if (process.env['NODE_ENV'] !== 'production') {
					// eslint-disable-next-line no-console
					console.error('[waeConnector] loadMore failed for query', query, err);
				}
				// Stop, and surface the error. A silent hang would leave the gallery
				// on skeleton cards, because `isAwaitingBackfill` never clears.
				loadingRef.current = false;
				setState((prev) => ({ ...prev, isLoading: false, error: err as Error }));
			});
	}, [executeQuery]);

	// Walk the full set one page at a time on idle.
	useEffect(() => {
		if (!autoLoad || state.done || state.isLoading || state.error) return;
		if (!nextQueryRef.current) return;
		return scheduleIdle(loadMore);
	}, [autoLoad, state.done, state.isLoading, state.error, state.items, loadMore]);

	return { ...state, loadMore };
};

function defaultKeyExtractor(item: unknown): string | undefined {
	if (typeof item !== 'object' || item === null) return undefined;
	const record = item as Record<string, unknown>;
	const sys = record['sys'];
	if (typeof sys === 'object' && sys !== null) {
		const id = (sys as Record<string, unknown>)['id'];
		if (typeof id === 'string') return id;
	}
	return undefined;
}
